/**
 * Approvals drawer — opened from the header "Review" action. Lists pending
 * message approvals with approve / reject, posting to
 * POST /message-approvals/:id/decide.
 */
import { useMemo, useState, type JSX } from 'react'
import { ShieldCheck } from 'lucide-react'
import { api } from '../../../api/client.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import type { Message, MessageApproval } from '../../../modules/conversations/types.js'
import { formatDateTime } from '../../../utils/timezone.js'
import ActionDrawer from './ActionDrawer.js'
import { GhostButton, PrimaryButton, R } from './primitives.js'

function ApprovalCard({
  approval,
  message,
  onDecided,
}: {
  approval: MessageApproval
  message: Message | undefined
  onDecided: () => void
}): JSX.Element {
  const toast = useToast()
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const decide = async (decision: 'approve' | 'reject'): Promise<void> => {
    setBusy(true)
    try {
      await api.post(`/message-approvals/${approval.id}/decide`, {
        decision,
        ...(decision === 'reject' ? { rejectionReason: reason.trim() || 'Rejected' } : {}),
      })
      toast.success(decision === 'approve' ? 'Message approved' : 'Message rejected')
      onDecided()
    } catch (e) {
      toast.error('Decision failed', e instanceof Error ? e.message : 'Unexpected error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`${R} border border-amber-200 bg-amber-50 p-3.5`}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-amber-700">
        Pending approval · {formatDateTime(approval.createdAt)}
      </div>
      <div className={`${R} border border-amber-100 bg-white px-3 py-2 text-sm text-slate-700`}>
        {message?.body || '(message content unavailable)'}
      </div>
      {rejecting ? (
        <div className="mt-2 space-y-2">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason for rejection…"
            className={`w-full ${R} border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#EEF4FF]`}
          />
          <div className="flex justify-end gap-2">
            <GhostButton onClick={() => setRejecting(false)}>Cancel</GhostButton>
            <button
              type="button"
              disabled={busy}
              onClick={() => void decide('reject')}
              className={`${R} bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50`}
            >
              Confirm reject
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex justify-end gap-2">
          <GhostButton onClick={() => setRejecting(true)} disabled={busy}>
            Reject
          </GhostButton>
          <PrimaryButton onClick={() => void decide('approve')} disabled={busy}>
            Approve
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}

export interface ApprovalsDrawerProps {
  open: boolean
  onClose: () => void
  pendingApprovals: MessageApproval[]
  messages: Message[]
  onDecided: () => void
}

export default function ApprovalsDrawer({
  open,
  onClose,
  pendingApprovals,
  messages,
  onDecided,
}: ApprovalsDrawerProps): JSX.Element {
  const messageById = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages])
  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Approvals"
      subtitle="Review outbound messages awaiting approval"
      icon={<ShieldCheck className="h-4 w-4" />}
      info={{
        lead: 'Messages an agent wrote that need a second pair of eyes before they leave the platform.',
        points: [
          { term: 'Approve', detail: 'Releases the message — it is sent on its channel as written.' },
          { term: 'Reject', detail: 'Blocks the send and returns it to the author to rework.' },
          { term: 'Scope', detail: 'These are message-level approvals. Conversation-level sign-off lives under Approval Requests.' },
        ],
      }}
    >
      {pendingApprovals.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">No pending approvals.</div>
      ) : (
        <div className="space-y-3">
          {pendingApprovals.map((a) => (
            <ApprovalCard
              key={a.id}
              approval={a}
              message={messageById.get(a.messageId)}
              onDecided={onDecided}
            />
          ))}
        </div>
      )}
    </ActionDrawer>
  )
}
